import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";

import GetAppIcon from "@mui/icons-material/GetApp";
import BlurOnOutlinedIcon from "@mui/icons-material/BlurOnOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import React, { useEffect, useState } from "react";
import {
  addContactIdsToList,
  getFileForSystemId,
  getProcessedFile,
} from "../../api/useractions";
import {
  acceptSuggestedChangesWithModifications,
  rollbackSuggestedChange,
  transitionSuggestedChange,
} from "../../api/suggestedchanges";

import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

import { ChickenOutModal } from "../utils/ChickenOutModal";
import { red, teal } from "@mui/material/colors";
import Modal from "@mui/material/Modal";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import CardActions from "@mui/material/CardActions";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import { IntegratedSystem } from "../../api/integratedsystems";
import { isEmpty } from "lodash";
import DownloadIcon from "@mui/icons-material/Download";
import { DataProject } from "../../api/dataprojects";
import {
  AccountNameAutocomplete,
  ListNameAutocomplete,
} from "../utils/datasources/AutoComplete";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

/**
 * callFunctionForSelectedNode Function
 *
 * This function executes a specified function for each selected node in the grid.
 *
 * @param {any} gridApi - The grid API for interacting with the grid.
 * @param {function} functionToCall - The function to call for each selected node.
 * @param {Array} functionArgs - Arguments to pass to the functionToCall.
 * @param {function} [onGridActionComplete] - Callback function to be executed after grid action.
 */
const callFunctionForSelectedNode = async (
  gridApi,
  functionToCall,
  functionArgs,
  onGridActionComplete?
) => {
  if (gridApi.getSelectedNodes().length > 0) {
    gridApi.setGridOption("loading", true);
    const nodeIds = [];
    // Build an array of selected node IDs

    gridApi.getSelectedNodes().map(async (node) => {
      nodeIds.push(node.data.id);
    });
    // Call the specified function for selected nodes

    await functionToCall(nodeIds, ...functionArgs);
    // Deselect all nodes and hide the overlay

    gridApi.deselectAll();
    gridApi.setGridOption("loading", false);
    // Execute onGridActionComplete callback if provided

    if (onGridActionComplete !== undefined) {
      onGridActionComplete(nodeIds);
    }
  }
};

/**
 * AddToMarketingCampaignButton Component
 *
 * This component provides a button that opens a modal to add selected items to a marketing list.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @param {IntegratedSystem|DataProject} props.integratedSystem - Integrated system or data project information.
 * @returns {JSX.Element} - Rendered component.
 */

export const AddToMarketingCampaignButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(): void;
    integratedSystem: IntegratedSystem | DataProject;
  }>
) => {
  const { rowsSelected, gridApi, integratedSystem } = props;
  // State for controlling modal visibility and marketing list name
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [marketingListName, setMarketingListName] = useState<string>("");
  const [listType, setListType] = useState<"new" | "existing">("new");
  const [selectedExistingListId, setSelectedExistingListId] =
    useState<string>(null);

  const resetForm = () => {
    setMarketingListName("");
    setListType("new");
    setSelectedExistingListId(null);
  };

  const closeModal = () => {
    resetForm();
    setModalOpen(false);
  };

  const handleAddToList = () => {
    const record_ids = [];
    // Collect CRM contact IDs of selected nodes
    gridApi.getSelectedNodes().forEach((node) => {
      if (node.data.crm_contact_id) {
        record_ids.push(node.data.crm_contact_id);
      }
    });

    const systemId = integratedSystem.hasOwnProperty("integrated_system_id")
      ? //@ts-ignore
        integratedSystem.integrated_system_id
      : integratedSystem.id;

    // Use either the entered new list name or the selected existing list name
    const listName =
      listType === "new" ? marketingListName : selectedExistingListId;

    // Call API to add contact IDs to the marketing list
    addContactIdsToList(systemId, record_ids, listName)
      .then(() => closeModal())
      .catch((err) => console.log(err));
  };

  return (
    <>
      {/* Modal for adding to marketing list */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm" style={{ padding: 0 }}>
          <Card style={{ padding: "16px" }}>
            <CardHeader
              title={"Add to marketing list"}
              titleTypographyProps={{ variant: "h3" }}
              action={
                <IconButton onClick={closeModal}>
                  <CloseOutlinedIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent>
              {/* Radio button group for list type selection */}
              <div style={{ marginBottom: "16px" }}>
                <Button
                  variant={listType === "new" ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  style={{ marginRight: "8px" }}
                  onClick={() => setListType("new")}
                >
                  Create new list
                </Button>
                <Button
                  variant={listType === "existing" ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  onClick={() => setListType("existing")}
                >
                  Add to existing list
                </Button>
              </div>

              {/* Input for new marketing list name or autocomplete for existing lists */}
              {listType === "new" ? (
                <TextField
                  data-testid={"new-marketing-list"}
                  required
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="dense"
                  label={"New marketing list name"}
                  value={marketingListName}
                  onChange={(event) => {
                    setMarketingListName(event.target.value);
                  }}
                />
              ) : (
                <div style={{ marginTop: "8px", marginBottom: "8px" }}>
                  <Typography variant={"h6"}>Select existing list:</Typography>

                  <ListNameAutocomplete
                    integratedSystemId={
                      integratedSystem.hasOwnProperty("integrated_system_id")
                        ? //@ts-ignore
                          integratedSystem.integrated_system_id
                        : integratedSystem.id
                    }
                    onSelectionChanged={(listId) =>
                      setSelectedExistingListId(listId)
                    }
                  />
                </div>
              )}
              <Divider style={{ marginTop: "16px" }} />
            </CardContent>
            <CardActions
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              {/* Button to add selected items to marketing list */}
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ margin: "2px", padding: "0em 0.5em" }}
                disabled={
                  (listType === "new" && !marketingListName) ||
                  (listType === "existing" && !selectedExistingListId)
                }
                onClick={handleAddToList}
              >
                Add to list
              </Button>
            </CardActions>
          </Card>
        </Container>
      </Modal>
      {/* Button to open the modal */}
      <Button
        variant="contained"
        color="primary"
        startIcon={<PlaylistAddCheckOutlinedIcon />}
        size="small"
        style={{ margin: "2px", padding: "0em 0.5em" }}
        onClick={() => setModalOpen(true)}
        disabled={rowsSelected === 0}
      >
        Add to list
      </Button>
    </>
  );
};

/**
 * MultiCompanyAssociationButton Component
 *
 * This component provides a button that opens a modal to bulk associate multiple contacts with a company.
 * It's specifically for use in the Employment Changes grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @param {IntegratedSystem|DataProject} props.integratedSystem - Integrated system or data project information.
 * @returns {JSX.Element} - Rendered component.
 */
export const MultiCompanyAssociationButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds: number[]): void;
    integratedSystem: IntegratedSystem | DataProject;
  }>
) => {
  const { rowsSelected, gridApi, integratedSystem, onGridActionComplete } =
    props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] =
    useState<{ name: string; crm_account_id: string }>();
  const [loading, setLoading] = useState<boolean>(false);

  const systemId = integratedSystem.hasOwnProperty("integrated_system_id")
    ? //@ts-ignore
      integratedSystem.integrated_system_id
    : integratedSystem.id;

  const resetForm = () => {
    setSelectedCompany(undefined);
  };

  const closeModal = () => {
    resetForm();
    setModalOpen(false);
  };

  const handleAssociate = async () => {
    if (!selectedCompany) return;

    setLoading(true);

    try {
      const selectedNodes = gridApi.getSelectedNodes();
      const nodeIds = [];
      //Process each selected node
      for (const node of selectedNodes) {
        //For employment changes, we're approving with the selected company as the choice
        if (node.data.id) {
          nodeIds.push(node.data.id);
          await acceptSuggestedChangesWithModifications(
            node.data.id,
            selectedCompany.name,
            selectedCompany.crm_account_id
          );
        }
      }

      gridApi.deselectAll();

      // Call the completion callback if provided
      if (onGridActionComplete) {
        onGridActionComplete(nodeIds);
      }

      closeModal();
    } catch (err) {
      console.error("Error associating contacts with company:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Modal for associating contacts with company */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm" style={{ padding: 0 }}>
          <Card style={{ padding: "16px" }}>
            <CardHeader
              title={"Associate with Company"}
              titleTypographyProps={{ variant: "h3" }}
              action={
                <IconButton onClick={closeModal}>
                  <CloseOutlinedIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent>
              <Typography variant="body1" style={{ marginBottom: "16px" }}>
                Select the company to associate with the selected contacts (
                {rowsSelected}).
              </Typography>

              <div style={{ marginTop: "16px", marginBottom: "16px" }}>
                <Typography variant={"h6"}>Company:</Typography>
                <AccountNameAutocomplete
                  integratedSystemId={systemId}
                  onSelectionChanged={setSelectedCompany}
                />
              </div>

              <Divider style={{ marginTop: "16px" }} />
            </CardContent>
            <CardActions
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ margin: "2px", padding: "0em 0.5em" }}
                disabled={!selectedCompany || loading}
                onClick={handleAssociate}
              >
                {loading ? "Processing..." : "Associate Company"}
              </Button>
            </CardActions>
          </Card>
        </Container>
      </Modal>

      {/* Button to open the modal */}
      <Button
        variant="contained"
        color="primary"
        startIcon={<BusinessIcon />}
        size="small"
        style={{ margin: "2px", padding: "0em 0.5em" }}
        onClick={() => setModalOpen(true)}
        disabled={rowsSelected === 0}
      >
        Multi Approve
      </Button>
    </>
  );
};

/**
 * ApproveChangesButton Component
 *
 * This component provides a button to approve selected suggested changes in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @returns {JSX.Element} - Rendered component.
 */
const ApproveChangesButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds: number[]): void;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete } = props;

  /**
   * Disable approve for INFORMATION tickets (they can only be rejected)
   */
  const hasInformationSelected =
    gridApi
      ?.getSelectedNodes?.()
      ?.some((node) => node.data?.change_type === "INFORMATION") ?? false;

  const isDisabled = rowsSelected === 0 || hasInformationSelected;

  /**
   * Approve suggested changes for selected nodes.
   */
  const approveSuggestedChanges = () => {
    callFunctionForSelectedNode(
      gridApi,
      transitionSuggestedChange,
      [],
      onGridActionComplete
    )
      .then(() => {})
      .catch((err) => console.log(err));
  };

  return (
    <Tooltip
      title={hasInformationSelected ? "Cannot approve Information tickets" : ""}
    >
      <span style={{ display: "inline-flex" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            <DoneAllOutlinedIcon
              style={{ color: isDisabled ? "" : teal["600"] }}
            />
          }
          size="small"
          style={{ margin: "2px", padding: "0em 0.5em" }}
          onClick={approveSuggestedChanges}
          disabled={isDisabled}
        >
          {`Approve(${rowsSelected})`}
        </Button>
      </span>
    </Tooltip>
  );
};

/**
 * RevertChangesButton Component
 *
 * This component provides a button to revert selected suggested changes in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @returns {JSX.Element} - Rendered component.
 */
const RevertChangesButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds: number[]): void;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete } = props;

  /**
   * Revert suggested changes for selected nodes.
   */

  const revertSuggestedChanges = async () => {
    const nodeIds = [];
    gridApi.getSelectedNodes().map(async (node) => {
      nodeIds.push(node.data.id);
    });
    gridApi.setGridOption("loading", true);
    Promise.all(nodeIds.map((node) => rollbackSuggestedChange(node))).then(
      (responses) => {
        gridApi.deselectAll();
        gridApi.setGridOption("loading", false);
        if (onGridActionComplete) {
          onGridActionComplete(nodeIds);
        }
      }
    );
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={
        <ReplayOutlinedIcon
          style={{ color: rowsSelected === 0 ? "" : teal["600"] }}
        />
      }
      size="small"
      style={{ margin: "2px", padding: "0em 0.5em" }}
      onClick={revertSuggestedChanges}
      disabled={rowsSelected === 0}
    >
      Revert
    </Button>
  );
};

/**
 * RejectChangesButton Component
 *
 * This component provides a button to reject selected suggested changes in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {function} [props.onGridActionComplete] - Callback triggered when the grid action is complete.
 * @returns {JSX.Element} - Rendered component.
 */
const RejectChangesButton = (
  props: Readonly<{
    gridApi: any;
    rowsSelected: number;
    onGridActionComplete?(nodeIds): void;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete } = props;
  /**
   * Reject suggested changes for selected nodes.
   */
  const rejectSuggestedChanges = async () => {
    callFunctionForSelectedNode(
      gridApi,
      transitionSuggestedChange,
      [true],
      onGridActionComplete
    )
      .then(() => {})
      .catch((err) => console.log(err));
  };

  const [chickenOutOpen, setChickenOutOpen] = useState<boolean>(false);
  return (
    <>
      {/* Reject button */}
      <Button
        variant="contained"
        color="primary"
        startIcon={
          <ClearOutlinedIcon
            style={{ color: rowsSelected === 0 ? "" : red["A400"] }}
          />
        }
        size="small"
        style={{ margin: "2px", padding: "0em 0.5em" }}
        onClick={() => setChickenOutOpen(true)}
        disabled={rowsSelected === 0}
      >
        {`Reject(${rowsSelected})`}
      </Button>
      {/* Chicken Out Modal */}

      <ChickenOutModal
        modalOpen={chickenOutOpen}
        title={"Are you sure you want to reject the change(s)?"}
        message={"Cirrom will not suggest this change anymore."}
        onModalOpenChange={setChickenOutOpen}
        onSuccess={() => {
          rejectSuggestedChanges()
            .then(() => {})
            .catch((err) => {
              console.log(err);
            });
        }}
      />
    </>
  );
};

/**
 * DetailsButton Component
 *
 * This component provides a button to view details of a selected row in the grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @returns {JSX.Element} - Rendered component.
 */
export const DetailsButton = (
  props: Readonly<{ gridApi: any; rowsSelected: number }>
) => {
  const { gridApi, rowsSelected } = props;

  /**
   * Handle button click to view details of a selected row.
   */
  const handleButtonClick = () => {
    if (gridApi.getSelectedNodes().length === 1) {
      gridApi.dispatchEvent({
        type: "rowDoubleClicked",
        node: gridApi.getSelectedNodes()[0],
      });
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<BlurOnOutlinedIcon />}
      size="small"
      style={{ margin: "2px", padding: "0em 0.5em" }}
      disabled={rowsSelected !== 1}
      onClick={handleButtonClick}
    >
      Details
    </Button>
  );
};

/**
 * ExportButton Component
 *
 * This component provides a button to initiate the export of a processed file.
 *
 * @param {object} props - Component props.
 * @param {number} props.fileId - The ID of the file to be exported.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @returns {JSX.Element} - Rendered component.
 */
const ExportButton = (props: Readonly<{ fileId: number; gridApi: any }>) => {
  const { fileId, gridApi } = props;

  /**
   * Initiates the export of the processed file.
   * Shows loading overlay while exporting.
   */
  const exportFile = () => {
    gridApi.setGridOption("loading", true);
    getProcessedFile(fileId)
      .then(
        () => gridApi.setGridOption("loading", false),
        () => gridApi.hideOverlay()
      )
      .catch((err) => console.log(err));
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<GetAppIcon />}
      size="small"
      style={{ margin: "2px", padding: "0em 0.5em" }}
      onClick={exportFile}
    >
      Export
    </Button>
  );
};

/**
 * SuggestedChangesRevertToolbar Component
 *
 * This component represents a toolbar for reverting suggested changes in a grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {function} [props.onGridActionComplete] - Callback function to be executed after grid action.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @returns {JSX.Element} - Rendered component.
 */
export const SuggestedChangesRevertToolbar = (
  props: Readonly<{
    gridApi: any;
    onGridActionComplete?(): void;
    rowsSelected: number;
    timeframe?: string;
    onTimeframeChange?: (timeframe: string) => void;
  }>
) => {
  const {
    gridApi,
    rowsSelected,
    onGridActionComplete,
    timeframe,
    onTimeframeChange,
  } = props;

  const timeframeOptions = [
    { name: "Last 7 Days", value: "last_7_days" },
    { name: "Last 30 Days", value: "last_30_days" },
    { name: "All Time", value: null },
  ];

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      {onTimeframeChange && (
        <ButtonGroup size="small" color="primary" style={{ margin: "2px" }}>
          {timeframeOptions.map((option) => (
            <Button
              key={option.name}
              variant={timeframe === option.value ? "contained" : "outlined"}
              onClick={() => onTimeframeChange(option.value)}
              style={{ padding: "0em 0.5em" }}
            >
              {option.name}
            </Button>
          ))}
        </ButtonGroup>
      )}

      {/* DetailsButton component */}
      <DetailsButton rowsSelected={rowsSelected} gridApi={gridApi} />

      {/* RevertChangesButton component */}
      <RevertChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
      />
    </div>
  );
};

/**
 * SuggestedChangesButtonsToolbar Component
 *
 * This component represents a toolbar for various actions related to suggested changes in a grid.
 *
 * @param {object} props - Component props.
 * @param {number} [props.fileId] - The ID of the file associated with the grid.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {function} [props.onGridActionComplete] - Callback function to be executed after grid action.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {IntegratedSystem | DataProject} [props.integratedSystem] - Integrated system or data project information.
 * @returns {JSX.Element} - Rendered component.
 */
export const SuggestedChangesButtonsToolbar = (
  props: Readonly<{
    fileId?: number;
    gridApi: any;
    onGridActionComplete?(nodeIds: number[]): void;
    rowsSelected: number;
    integratedSystem?: IntegratedSystem | DataProject;
    dataPoint?: string;
    currentTab?: string;
    timeframe?: string | null;
    onTimeframeChange?: (tf: string | null) => void;
  }>
) => {
  const {
    fileId,
    gridApi,
    rowsSelected,
    onGridActionComplete,
    integratedSystem,
    dataPoint,
    currentTab,
    timeframe,
    onTimeframeChange,
  } = props;

  const [localFileId, setLocalFileId] = useState<number>(undefined);

  useEffect(() => {
    // Fetch the file associated with the integrated system if not provided

    if (
      !fileId &&
      integratedSystem &&
      "type" in integratedSystem &&
      integratedSystem?.type?.indexOf("CRM") === -1
    ) {
      getFileForSystemId(integratedSystem.id)
        .then((fileResponse) => {
          if (fileResponse) {
            setLocalFileId(fileResponse.id);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [fileId, integratedSystem]);

  const timeframeOptions = [
    { name: "Last 7 Days", value: "last_7_days" },
    { name: "Last 30 Days", value: "last_30_days" },
    { name: "All Time", value: null },
  ];

  // Determine if we're in the Employment Changes grid
  const isEmploymentGrid = dataPoint === "EMPLOYMENT";
  // For company tab specifically we add the associate company button
  const isCompanyTab =
    isEmploymentGrid && (currentTab === "company" || currentTab === "all");

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      {onTimeframeChange && (
        <ButtonGroup size="small" color="primary" style={{ margin: "2px" }}>
          {timeframeOptions.map((option) => (
            <Button
              key={option.name}
              variant={timeframe === option.value ? "contained" : "outlined"}
              onClick={() => onTimeframeChange(option.value)}
              style={{ padding: "0em 0.5em" }}
            >
              {option.name}
            </Button>
          ))}
        </ButtonGroup>
      )}
      {/* DetailsButton component */}
      <DetailsButton rowsSelected={rowsSelected} gridApi={gridApi} />

      {/* ApproveChangesButton component */}
      <ApproveChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
      />
      {/* RejectChangesButton component */}
      <RejectChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
      />
      {/* Conditionally render ExportButton based on file and integrated system */}
      {fileId ||
      (integratedSystem && integratedSystem.system_type === "EXCEL") ? (
        <ExportButton fileId={fileId || localFileId} gridApi={gridApi} />
      ) : null}
      {integratedSystem && integratedSystem.system_type === "INTERACTION" && (
        <AddToMarketingCampaignButton
          gridApi={gridApi}
          rowsSelected={rowsSelected}
          integratedSystem={integratedSystem}
        />
      )}
      {/* Show Company Association Button for Employment Changes */}
      {isCompanyTab && integratedSystem && (
        <MultiCompanyAssociationButton
          gridApi={gridApi}
          rowsSelected={rowsSelected}
          onGridActionComplete={onGridActionComplete}
          integratedSystem={integratedSystem}
        />
      )}
    </div>
  );
};

/**
 * RuleEvaluationButtonsToolbar Component
 *
 * This component represents a toolbar for various actions related to rule evaluation in a grid.
 *
 * @param {object} props - Component props.
 * @param {any} props.gridApi - The grid API for interacting with the grid.
 * @param {function} [props.onGridActionComplete] - Callback function to be executed after grid action.
 * @param {number} props.rowsSelected - The number of selected rows in the grid.
 * @param {IntegratedSystem} [props.integratedSystem] - Integrated system information.
 * @returns {JSX.Element} - Rendered component.
 */
export const RuleEvaluationButtonsToolbar = (
  props: Readonly<{
    gridApi: any;
    onGridActionComplete?(nodeIds): void;
    rowsSelected: number;
    integratedSystem?: IntegratedSystem;
  }>
) => {
  const { gridApi, rowsSelected, onGridActionComplete, integratedSystem } =
    props;

  return (
    <div style={{ display: "inline-flex", alignItems: "center" }}>
      {/* DetailsButton component */}
      <DetailsButton rowsSelected={rowsSelected} gridApi={gridApi} />
      {/* RejectChangesButton component */}
      <RejectChangesButton
        gridApi={gridApi}
        rowsSelected={rowsSelected}
        onGridActionComplete={onGridActionComplete}
      />

      {integratedSystem && integratedSystem.system_type === "INTERACTION" && (
        <AddToMarketingCampaignButton
          gridApi={gridApi}
          rowsSelected={rowsSelected}
          integratedSystem={integratedSystem}
        />
      )}
      {/* Conditionally render AddToMarketingCampaignButton for INTERACTION system */}
      {!isEmpty(gridApi) && (
        <IconButton
          color={"info"}
          component={"span"}
          onClick={() => {
            gridApi.exportDataAsCsv();
          }}
          style={{ height: "20px", width: "20px" }}
        >
          <DownloadIcon />
        </IconButton>
      )}
    </div>
  );
};
